import React, { useState, useEffect, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { InView } from 'react-intersection-observer';
import { withEndlessScroll } from '../../../HOC/endlessScroll';
import { setError } from '../../../../actions';
import { AgencyFilterResultsList } from '../AgencyFilterResultsList';
import { ItemCard } from '../../../ui/ItemCard';
import { useBookmarks } from '../../../../hooks/useBookmarks';
import { filteredAgenciesQuery } from '../../../../api/queries';
import {
  FilteredAgenciesQuery,
  ItemConnectionSort
} from '../../../../graphql/graphql';
import { AgencyList } from '../styled';
import { MobileShowAllWrapper, ResultBlockContainer } from './styled';
import { getFilterParamsToGetAgencyItems } from '../../../../utils/helpers/item';
import { AgencyFilterResultsBlockProps } from './types';
import { ShowAllButton } from './ShowAllButton';
import { AgencyFilterResultBlockTitle } from './ResultBlockTitle';
import {
  EndlessItemsPlaceholder,
  endlessItemsPlaceholderCards
} from '../../../Placeholders/EndlessItemsPlaceholder';

const EndlessSearchItems = withEndlessScroll(AgencyFilterResultsList);
const SSR_BLOCKS_COUNT = 2;

export const AgencyFilterResultsBlock: React.FC<AgencyFilterResultsBlockProps> =
  memo(
    ({
      title,
      category,
      agencyId,
      activeCategoryId,
      isLeased,
      limit,
      cardsLimit,
      isCurrentItemPage,
      activeLeasedCategory,
      showAllHref,
      onClickItem,
      blockIndex
    }) => {
      const filterResultsBlockRef = useRef<HTMLDivElement>(null);
      const isSortingChange = useRef(false);

      const { t } = useTranslation();
      const dispatch = useDispatch();
      const { isBookmarked } = useBookmarks();

      const [skipRequest, setSkipRequest] = useState(
        isCurrentItemPage || blockIndex > SSR_BLOCKS_COUNT
      );
      const [sortingValue, setSortingValue] = useState(
        ItemConnectionSort.BumpedAtDesc
      );

      const variables = {
        filter: getFilterParamsToGetAgencyItems(
          isLeased,
          agencyId ?? '',
          category.id,
          activeCategoryId
        ),
        first: limit,
        sort: sortingValue
      };

      const { loading, data, error, fetchMore } =
        useQuery<FilteredAgenciesQuery>(filteredAgenciesQuery, {
          variables,
          skip: skipRequest,
          notifyOnNetworkStatusChange: true,
          ...(isCurrentItemPage
            ? { ssr: false, fetchPolicy: 'network-only' as const }
            : {})
        });

      const result = data?.itemsConnection?.edges || [];
      const totalCount = data?.itemsConnection?.totalCount;
      const isSortingLoading = loading && isSortingChange.current;

      useEffect(() => {
        if (error) dispatch(setError(true));
      }, [error]);

      useEffect(() => {
        if (!loading) isSortingChange.current = false;
      }, [loading]);

      if (totalCount === 0) {
        return null;
      }

      const showAllResultHandler = () => {
        if (activeCategoryId === 'all') {
          onClickItem(category.id);
        } else {
          onClickItem(category.id, false, [activeCategoryId]);
        }
      };

      const showAllAdsText = t('pages.agency.show_all_results_many', {
        count: totalCount
      });
      const isShowSorting =
        !isCurrentItemPage && activeLeasedCategory.length === 1;
      const displayedResult =
        isCurrentItemPage || isShowSorting ? result : result.slice(0, limit);
      const isShowButton =
        !isShowSorting &&
        displayedResult.length === limit &&
        (totalCount || 0) > limit;

      const isInitialLoading = loading && !displayedResult.length;

      const itemsPlaceholder = (
        <EndlessItemsPlaceholder count={limit} container={AgencyList} />
      );
      const itemsPlaceholderCards = endlessItemsPlaceholderCards(limit);

      const itemCards = displayedResult.map(item => (
        <ItemCard
          key={item?.node?.id}
          {...item?.node}
          inBookmarks={isBookmarked(item?.node?.id!)}
          stat="agency-item-card"
          bookmarkStat="agency-item-card-bookmark"
        />
      ));

      return (
        <InView
          as="div"
          rootMargin="400px"
          triggerOnce
          onChange={inView => {
            if (inView) {
              setSkipRequest(false);
            }
          }}
        >
          <ResultBlockContainer data-cy="agency-filter__results-block">
            <AgencyFilterResultBlockTitle
              title={title}
              showAllAdsText={showAllAdsText}
              showAllHref={showAllHref}
              loading={isInitialLoading}
              isSortingLoading={isSortingLoading}
              isShowButton={isShowButton}
              isShowSorting={isShowSorting}
              sortingValue={sortingValue}
              showAllResultHandler={showAllResultHandler}
              onSortChange={sortValue => {
                isSortingChange.current = true;
                setSortingValue(sortValue);
              }}
            />

            {isInitialLoading && itemsPlaceholder}

            {!!displayedResult.length && (
              <EndlessSearchItems
                items={itemCards}
                loading={loading}
                ref={filterResultsBlockRef}
                forwardedRef={filterResultsBlockRef}
                loadingPlaceholder={itemsPlaceholderCards}
                isStopLoad={
                  !data?.itemsConnection?.pageInfo?.hasNextPage ||
                  limit === cardsLimit
                }
                onLoadMore={() => {
                  fetchMore({
                    variables: {
                      filter: getFilterParamsToGetAgencyItems(
                        isLeased,
                        agencyId ?? '',
                        category.id,
                        activeCategoryId
                      ),
                      first: limit,
                      cursor: data?.itemsConnection?.pageInfo?.endCursor
                    }
                  });
                }}
              >
                {itemCards}
              </EndlessSearchItems>
            )}

            {isShowButton && (
              <MobileShowAllWrapper>
                <ShowAllButton
                  text={showAllAdsText}
                  href={showAllHref}
                  onClick={showAllResultHandler}
                />
              </MobileShowAllWrapper>
            )}
          </ResultBlockContainer>
        </InView>
      );
    }
  );

AgencyFilterResultsBlock.displayName = 'AgencyFilterResultsBlock';
